import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import FeedbackRatingIcon, {
  FeedbackRating,
  RESPONSE_COLUMN_WIDTH_PX,
} from 'components/FeedbackRatingIcon'
import FeedbackResolvedByCell, {
  ResolvedByValue,
} from 'components/FeedbackResolvedByCell'
import SortableColumnHeader, {
  SortDirection,
} from 'components/SortableColumnHeader'
import { buildTranslate } from 'locales'
import moment from 'moment-timezone'
import React from 'react'
import { Link } from 'react-router-dom'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'

const t = buildTranslate('components.feedback_table')

// Alias of the shared rating union so the icon component and the table can never
// drift apart. Kept exported under the old name for existing consumers.
export type FeedbackTableRating = FeedbackRating

// One normalized view-model both surfaces map their wire rows into — the table
// never sees Activity or OrderRating. Every per-surface difference is plain data
// (a path string, message strings), never JSX, which is how the shared table
// stays legal under the "never pass JSX trees as props" rule.
//
// Intentional divergence from today's 7-col ActivityTable (redesign, 2026-08-05):
// the Order-number column is dropped (nexus ships order_number: nil) and the old
// Needs-Attention "Status" mode folds into the scene-level Hide-resolved query
// filter — so this row carries no status/orderNumber field by design. W3 deletes
// ActivityTable and feeds these plain-data props; it does not widen this API.
export type FeedbackTableRow = {
  id: string // row key — never the array index
  rating: FeedbackTableRating // W3 always passes 'down'; W4 passes the real value
  date: string // ISO8601 as it arrives; the table formats MM/DD/YYYY
  customerName: string | null // PII-minimized upstream (first_name_last_initial); NULL when the user was deleted — both wires nil-guard it
  locationName: string | null // NULLABLE on the TUTD side when the rating has no conversation
  resolvedBy: ResolvedByValue
  conversationId: string | null // NULLABLE for the same reason; only getViewPath reads it
  // The customer's free-text note. Null when they left none — and always null
  // on the RAI Escalations surface, whose wire has no equivalent field (which is
  // why the Comment column is opt-in via showCommentColumn rather than always
  // on). It also rides along so the View link can hand the whole row to the
  // destination page (see FeedbackTableLinkState) for a first paint that doesn't
  // wait on a fetch.
  comment: string | null
  // The rating's customer (stringified int). Like `comment`, the table never
  // renders it — it rides in the handed-over row so the destination page can
  // query that customer's profile and other ratings immediately. Null on the
  // RAI Escalations surface, whose wire has no equivalent field.
  userId: string | null
  // users.identifier — the customer-profile lookup/link key (a raw numeric id
  // 404s on GET /users/:identifier). Same ride-along role as userId.
  userUid: string | null
}

// What the View link puts in router state. The destination reads it with
// react-router's useLocation<FeedbackTableLinkState>() and must treat `row` as
// optional — a deep link or a refresh arrives with no state at all.
export type FeedbackTableLinkState = {
  row?: FeedbackTableRow
}

type Props = {
  rows: FeedbackTableRow[]
  isLoading: boolean
  isError: boolean
  onRetry: () => void

  // Controlled sort — the only sortable column is Date.
  sortDirection: SortDirection
  onSortChange: (direction: 'asc' | 'desc') => void

  // Per-surface differences, all plain data:
  getViewPath: (row: FeedbackTableRow) => string | null
  emptyMessage: string
  filteredEmptyMessage: string
  errorMessage: string
  hasActiveFilters: boolean

  // Opt-in per surface, mirroring getViewPath: only a destination that reads
  // router state (the Details tab's RatingDetail) should supply this. When
  // absent, the View link is a plain path string — RAI Escalations' CaseDetail
  // never reads a row, so it must not carry the customer name / free-text
  // comment into window.history.state.
  // May also return undefined per row: a surface can opt in for the rows whose
  // destination reads the state and still hand a plain path to the rest (the
  // Details tab's conversation-backed rows link into RAI CaseDetail).
  getLinkState?: (row: FeedbackTableRow) => FeedbackTableLinkState | undefined

  // Opt-in per surface, mirroring getViewPath/getLinkState: renders the Figma
  // subtle "THANX AI" chip (light spearmint10 pill + trailing sparkle cluster)
  // in the Resolved-by column instead of the legacy solid pill. Both current
  // surfaces opt in — the TUTD Details tab (Figma h4mcORhHQEGfWaJ6yrtiDf,
  // node 659-9447) and RAI Escalations (Figma 2L1kCbUYlhHJqhmtNt0kpW, node
  // 2318:64225). Defaults to solid so a future surface that doesn't opt in
  // renders exactly as this component did before either restyle.
  subtleResolvedBadge?: boolean

  // Opt-in per surface, mirroring the props above: renders the customer's
  // free-text Comment column between Customer and Location. Only the TUTD Details
  // tab opts in — RAI Escalations' wire has no comment field at all (its mapper
  // hardcodes `comment: null`), so an always-on column would be a full column of
  // em-dashes there.
  //
  // Deliberately a prop and NOT inferred from the data (`rows.some(r =>
  // r.comment)`): a page of genuinely comment-less ratings would make the column —
  // and every other column's width under table-layout:fixed — flicker in and out
  // as the merchant pages through.
  showCommentColumn?: boolean

  // Opt-OUT per surface, mirroring showCommentColumn but inverted: RAI
  // Escalations keeps the Location column (its long-standing behavior), while
  // the TUTD Details tab hides it — order-feedback ratings on that tab have no
  // location dimension worth showing. Defaults to true, so a surface that
  // doesn't pass this prop renders exactly as it did before.
  showLocationColumn?: boolean

  // Opt-OUT per surface, mirroring showLocationColumn: the customer-profile
  // Order feedback tab (AI-1288) already scopes every row to one customer, so
  // a Customer column repeating that same name on every row is redundant —
  // FR-30 explicitly omits it. Defaults to true, so every existing caller (the
  // Details tab, RAI Escalations) keeps rendering Customer exactly as before
  // without passing this prop.
  showCustomerColumn?: boolean
}

// Keep in sync with the <th> count — skeleton rows + empty/error colSpan use it.
// Comment (opt-in), Location (opt-out) and Customer (opt-out) are all
// conditional, so the effective count is computed per render (see columnCount
// below); this is the count of the columns every surface always renders:
// Response, Date, Resolved-by, View.
const BASE_COLUMN_COUNT = 4
const SKELETON_ROW_COUNT = 5
// shared by every nullable cell (Customer, Comment, Location)
const EMPTY_CELL_PLACEHOLDER = '—'
// Comment cell: wraps to this many lines before ellipsizing.
const COMMENT_LINE_CLAMP = 2
// Fixed line-height (px) the clamp above computes against, so the 2-line box
// height doesn't depend on the font's default line-height.
const COMMENT_LINE_HEIGHT_PX = 20

// Pinned column widths + table-layout:fixed freeze the layout so cell content
// never resizes a column. Without this the table reflows: the Resolved-by cell
// toggles between the THANX-AI badge and an empty cell as "Hide resolved" flips,
// and location names vary in length across pages.
//
// INVARIANT: exactly one <col> per surface is left unpinned — whichever of
// Comment/Location that surface currently shows. Customer keeps a pinned width
// whenever it renders: the API PII-minimizes it to `first_name_last_initial` on
// every surface (see FeedbackTableRow.customerName), so it has a genuinely
// bounded width and none of that column's old slack should go anywhere but the
// surface's flex column. Comment and Location never carry a pinned width — each
// is a bare, unpinned <col /> whenever its surface renders it (showCommentColumn
// / showLocationColumn) and absent otherwise. The Details tab (Comment shown,
// Location hidden) leaves Comment as its flex column; RAI Escalations (Location
// shown, Comment hidden) leaves Location as its flex column, exactly as it
// behaves today. Customer is now ALSO opt-out (showCustomerColumn, AI-1288's
// customer-profile surface hides it), but that never changes which column is
// the unpinned one — it only removes a pinned <col>/<th>/<td> entirely, same as
// Location does when hidden.
//
// Do NOT put a pinned width on Comment or Location, and do NOT let a surface end
// up with zero unpinned <col>s: under table-layout:fixed,
// once every column has an explicit width, the browser distributes any
// difference between the pinned sum and the table's actual width
// *proportionally across all columns* — including Resolved-by. That would
// compress Resolved-by below the 288px the THANX-AI badge needs to avoid
// clipping, on any viewport narrower than the pinned sum. Keeping one column
// unpinned per surface is what keeps the other five pixel-exact regardless of
// viewport.
//
// The Response width comes from components/FeedbackRatingIcon so this table and
// RecentActivitiesTable pin the thumb column to the same value.
const COLUMN_WIDTH_PX = {
  response: RESPONSE_COLUMN_WIDTH_PX,
  date: 152,
  // Bounded by the PII-minimized `first_name_last_initial` format (e.g.
  // "Domitila J."). 120px of content (168 minus the cell's 48px horizontal
  // padding) comfortably fits that pattern for a long first name ("Christopher
  // J." ≈ 108px at 14px type) without clipping.
  customer: 168,
  // Figma's TUTD spec is 287px, but our Resolved-by cell renders a wider
  // THANX-AI badge + sparkle icon than the Figma mock — 288px avoids clipping it.
  resolvedBy: 288,
  view: 92,
}

// MM/DD/YYYY, no time — the table's own column format, distinct from the
// long-form date/time in utilities/dateFormatters that the detail pages use.
// Invalid input passes through unchanged (mirrors ActivityTable).
//
// The leading falsy guard is load-bearing: moment(undefined) is "valid" and equals
// NOW, so a missing timestamp at the API boundary would otherwise render today's
// date. Mirrors utilities/dateFormatters.
export const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return ''
  const parsed = moment(iso)
  return parsed.isValid() ? parsed.format('MM/DD/YYYY') : iso
}

const FeedbackTable = ({
  rows,
  isLoading,
  isError,
  onRetry,
  sortDirection,
  onSortChange,
  getViewPath,
  emptyMessage,
  filteredEmptyMessage,
  errorMessage,
  hasActiveFilters,
  getLinkState,
  subtleResolvedBadge = false,
  showCommentColumn = false,
  showLocationColumn = true,
  showCustomerColumn = true,
}: Props) => {
  const [css] = useStyletron()

  // ENFORCES the invariant COLUMN_WIDTH_PX documents: under table-layout:fixed
  // at least one <col> must stay unpinned to absorb the surface's slack or
  // deficit. Comment and Location are the only two unpinned columns, and they
  // are independent booleans — so "neither" is a reachable combination that
  // leaves every column pinned, and the browser then compresses all of them to
  // fit, including the 288px Resolved-by needs for the THANX AI badge.
  //
  // A comment alone did not hold this: the combination was not only reachable,
  // four tests in this component's own suite were asserting the five-column
  // all-pinned layout as if it were supported. Dev/test-only (stripped from the
  // production bundle) and a throw rather than a console.* — a warning in a test
  // run is a warning nobody reads, and no-console is a repo rule. No shipping
  // surface passes this combination, so this cannot fire in a real environment.
  if (
    process.env.NODE_ENV !== 'production' &&
    !showCommentColumn &&
    !showLocationColumn
  ) {
    throw new Error(
      'FeedbackTable: showCommentColumn and showLocationColumn cannot both be off — ' +
        'one of them must stay unpinned to absorb the table’s slack under ' +
        'table-layout:fixed, or every column (including Resolved-by) gets ' +
        'compressed. Show at least one, or pin a different column deliberately.'
    )
  }

  // Keep in sync with the <th> list AND the <colgroup> below — skeleton rows and
  // the empty/error colSpan both span it. Comment (opt-in), Location (opt-out)
  // and Customer (opt-out) are independent, so all three add/subtract from the
  // base count.
  const columnCount =
    BASE_COLUMN_COUNT +
    (showCommentColumn ? 1 : 0) +
    (showLocationColumn ? 1 : 0) +
    (showCustomerColumn ? 1 : 0)

  const headerCellStyle = {
    padding: '14px 24px',
    fontWeight: 700,
    fontSize: '14px',
    color: merchantTheme.colors.grey90,
    borderBottom: `1px solid ${merchantTheme.colors.grey30}`,
    textAlign: 'left',
  } as const

  const headerCellClass = css(headerCellStyle)

  const cellClass = css({
    padding: '22px 24px',
    fontSize: '14px',
    color: merchantTheme.colors.grey70,
    backgroundColor: merchantTheme.colors.grey05,
    borderBottom: `1px solid ${merchantTheme.colors.grey30}`,
  })

  // Response column: header AND cell are both centered, so the glyph sits
  // exactly under the "Response" label however the browser distributes width.
  // Centering only the cell (the previous rule) left the label hugging the
  // column's left edge with the thumb floating mid-column. The column itself is
  // pinned to the shared RESPONSE_COLUMN_WIDTH_PX, which is what keeps that
  // alignment identical here and in RecentActivitiesTable.
  //
  // The header overrides textAlign by re-declaring the whole style object rather
  // than concatenating a second atomic class: the base header already declares
  // `text-align: left`, and two competing atomic classes are resolved by
  // styletron's injection order, not by the order they appear in `className`.
  // The cell has no textAlign of its own, so there it can safely compose.
  const responseHeaderClass = css({ ...headerCellStyle, textAlign: 'center' })
  const responseCellClass = `${cellClass} ${css({ textAlign: 'center' })}`

  // Comment column: clipped to COMMENT_LINE_CLAMP lines with a trailing
  // ellipsis. The truncation is CSS-only on purpose — the full comment stays in
  // the DOM, so a screen reader still gets all of it.
  //
  // The clamp properties (display/-webkit-box-orient/-webkit-line-clamp) live on
  // an inner wrapper <span> inside the <td>, NOT on the <td> itself. The table is
  // table-layout:fixed, and overriding a cell's `display` away from its default
  // `table-cell` (which `display: -webkit-box` does) pulls it out of the table
  // formatting context — that reflows every column's width for the whole row.
  // Don't "simplify" this back onto the cell.
  const commentWrapperClass = css({
    display: '-webkit-box',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: COMMENT_LINE_CLAMP,
    overflow: 'hidden',
    // Fixed so the clamp's 2-line box height is predictable regardless of the
    // font's default line-height.
    lineHeight: `${COMMENT_LINE_HEIGHT_PX}px`,
  })

  // Early-return body states rather than a 3-branch nested ternary in the JSX
  // (a repo convention). Loading → skeletons, no rows → empty/filtered copy,
  // otherwise the data rows.
  const renderRows = () => {
    if (isLoading) {
      return Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
        <tr
          key={`skeleton-row-${i}`}
          data-testid="feedback-skeleton-row"
          aria-hidden="true"
        >
          {Array.from({ length: columnCount }).map((__, j) => (
            <td key={`skeleton-cell-${i}-${j}`} className={cellClass}>
              <div
                className={css({
                  height: '14px',
                  borderRadius: '4px',
                  backgroundColor: merchantTheme.colors.grey20,
                })}
              />
            </td>
          ))}
        </tr>
      ))
    }

    if (rows.length === 0) {
      return (
        <tr>
          <td colSpan={columnCount}>
            <div className={css({ padding: '48px 0', textAlign: 'center' })}>
              <Text.BodyText3 color="grey50">
                {hasActiveFilters ? filteredEmptyMessage : emptyMessage}
              </Text.BodyText3>
            </div>
          </td>
        </tr>
      )
    }

    return rows.map(row => {
      const viewPath = getViewPath(row)
      // Coalesce once so the visible cell AND the View aria-label share one
      // placeholder — a null customer never renders as literal "View null".
      const customerName = row.customerName ?? EMPTY_CELL_PLACEHOLDER
      const linkState = getLinkState?.(row)
      // Truthiness (not a null check) so a comment that is present but empty
      // falls to the em-dash too — a pair of empty quotes reads as a bug.
      const commentCell = row.comment
        ? t('comment_quoted', { comment: row.comment })
        : EMPTY_CELL_PLACEHOLDER
      return (
        <tr key={row.id}>
          <td className={responseCellClass}>
            <FeedbackRatingIcon rating={row.rating} />
          </td>
          <td className={cellClass}>{formatShortDate(row.date)}</td>
          {/* Opt-out — keep this condition identical to the Customer <col> and
              <th> below. The aria-label a few lines down still reads
              `customerName` regardless: it's a screen-reader-only identifier
              for the View link, not this visible cell, so hiding the column
              doesn't remove the row's accessible name. */}
          {showCustomerColumn && <td className={cellClass}>{customerName}</td>}
          {showCommentColumn && (
            <td className={cellClass}>
              <span className={commentWrapperClass}>{commentCell}</span>
            </td>
          )}
          {showLocationColumn && (
            <td className={cellClass}>
              {row.locationName ?? EMPTY_CELL_PLACEHOLDER}
            </td>
          )}
          <td className={cellClass}>
            {/* The cell owns the badge / operator-name / fallback decision; passing
                the sr-only note as the fallback keeps "exactly one renders" structural
                rather than duplicating the cell's branch here (an unresolved row is
                visually empty, so the note is what a screen reader hears). */}
            <FeedbackResolvedByCell
              resolvedBy={row.resolvedBy}
              fallback={<span className="sr-only">{t('not_resolved')}</span>}
              subtleResolvedBadge={subtleResolvedBadge}
            />
          </td>
          <td className={cellClass}>
            {viewPath && (
              <Link
                // Object form only when the surface opts in via getLinkState: the
                // destination paints from the row immediately instead of blocking
                // on its own fetch. Every surface's destination must still work
                // without it (refresh/deep link never has state), and a surface
                // that never opts in (RAI Escalations) gets a plain path string —
                // no row goes into window.history.state.
                to={
                  linkState
                    ? { pathname: viewPath, state: linkState }
                    : viewPath
                }
                className={css({
                  color: merchantTheme.colors.belizeHole50,
                  fontWeight: 400,
                  textDecoration: 'none',
                })}
                aria-label={t('view_aria', { customer: customerName })}
              >
                {t('headers.view')}
              </Link>
            )}
          </td>
        </tr>
      )
    })
  }

  if (isError) {
    // role="alert" (implicit aria-live="assertive") so a loading→error swap is
    // announced — the table replaces itself, so nothing else moves focus here.
    return (
      <div
        role="alert"
        className={css({ padding: '48px 0', textAlign: 'center' })}
      >
        <Text.BodyText3 color="grey70" className="mb-s">
          {errorMessage}
        </Text.BodyText3>
        <Button kind="secondary" onClick={onRetry}>
          {t('retry')}
        </Button>
      </div>
    )
  }

  return (
    <table
      // Skeleton rows are aria-hidden, so without this a screen reader sees headers
      // and zero rows with no "data coming" signal — the symmetric partner to the
      // error state's role="alert".
      aria-busy={isLoading}
      className={css({
        width: '100%',
        borderCollapse: 'collapse',
        tableLayout: 'fixed',
      })}
    >
      {/* This <col> list MUST match the <th> list below one-for-one, in order —
          under table-layout:fixed a colgroup that's off by one silently shifts
          every column's width. The Comment, Location and Customer <col>s are
          therefore each rendered under the exact same condition as their
          respective <th>. */}
      <colgroup>
        <col className={css({ width: `${COLUMN_WIDTH_PX.response}px` })} />
        <col className={css({ width: `${COLUMN_WIDTH_PX.date}px` })} />
        {/* Customer: pinned when shown (PII-minimized to a bounded
            first_name_last_initial), absent entirely on the customer-profile
            surface (showCustomerColumn=false) — see the INVARIANT comment on
            COLUMN_WIDTH_PX. */}
        {showCustomerColumn && (
          <col className={css({ width: `${COLUMN_WIDTH_PX.customer}px` })} />
        )}
        {/* Comment: unpinned, and the Details tab's flex column when shown (see
            the INVARIANT comment on COLUMN_WIDTH_PX). */}
        {showCommentColumn && <col />}
        {/* Location: unpinned, and RAI Escalations' flex column when shown. */}
        {showLocationColumn && <col />}
        <col className={css({ width: `${COLUMN_WIDTH_PX.resolvedBy}px` })} />
        <col className={css({ width: `${COLUMN_WIDTH_PX.view}px` })} />
      </colgroup>
      <thead>
        <tr>
          <th scope="col" className={responseHeaderClass}>
            {t('headers.response')}
          </th>
          <SortableColumnHeader
            label={t('headers.date')}
            sortAriaLabel={t('sort_by_date')}
            direction={sortDirection}
            onSort={() =>
              onSortChange(sortDirection === 'asc' ? 'desc' : 'asc')
            }
            className={headerCellClass}
          />
          {/* Opt-out — keep this condition identical to the Customer <col> above. */}
          {showCustomerColumn && (
            <th scope="col" className={headerCellClass}>
              {t('headers.customer')}
            </th>
          )}
          {/* Opt-in — keep this condition identical to the Comment <col> above. */}
          {showCommentColumn && (
            <th scope="col" className={headerCellClass}>
              {t('headers.comment')}
            </th>
          )}
          {/* Opt-out — keep this condition identical to the Location <col> above. */}
          {showLocationColumn && (
            <th scope="col" className={headerCellClass}>
              {t('headers.location')}
            </th>
          )}
          <th scope="col" className={headerCellClass}>
            {t('headers.resolved_by')}
          </th>
          <th scope="col" className={headerCellClass}>
            <span className="sr-only">{t('headers.view')}</span>
          </th>
        </tr>
      </thead>
      <tbody>{renderRows()}</tbody>
    </table>
  )
}

export default FeedbackTable
